// Pure pagination helpers, ported from V1 -- no React, no I/O. The ingredient
// passes the current page and the server total in and renders the window.
// current page + server total in and renders the returned window.

// Rows per page. V1 uses 25; ours has always asked the API for 50 and
// changing it here would change what the server is asked for, not just how the
// buttons are drawn.
export const PAGE_SIZE = 50

/** Number of pages for a given total + page size. Always >= 1 (an empty list is one page). */
export function totalPages(total: number, pageSize: number = PAGE_SIZE): number {
  if (total <= 0 || pageSize <= 0) return 1
  return Math.ceil(total / pageSize)
}

/** Clamp a page into the valid [1, totalPages] range. */
export function clampPage(page: number, totalPageCount: number): number {
  if (totalPageCount < 1) return 1
  return Math.min(Math.max(Math.trunc(page), 1), totalPageCount)
}

export type PaginationItem =
  | { readonly type: 'page'; readonly page: number }
  | { readonly type: 'ellipsis'; readonly key: string }

/**
 * Build the page-button window with ellipses (e.g. `1 … 4 5 6 … 9`).
 *
 * Always shows the first and last page and a sibling window around the current page.
 * A single-page gap is filled with the page itself (no lone "…" standing in for one page);
 * a wider gap becomes a single ellipsis marker. Pure and deterministic.
 */
export function paginationWindow(
  current: number,
  total: number,
  siblingCount: number = 1,
  boundaryCount: number = 1,
): readonly PaginationItem[] {
  if (total <= 0) return []
  const currentClamped = Math.min(Math.max(Math.trunc(current), 1), total)

  // Small totals fit without any ellipsis — show every page.
  if (total <= boundaryCount * 2 + siblingCount * 2 + 3) {
    return Array.from({ length: total }, (_unused, i) => ({ type: 'page', page: i + 1 }))
  }

  const pages = new Set<number>()
  for (let i = 1; i <= Math.min(boundaryCount, total); i++) pages.add(i)
  for (let i = Math.max(total - boundaryCount + 1, 1); i <= total; i++) pages.add(i)
  for (
    let i = Math.max(currentClamped - siblingCount, 1);
    i <= Math.min(currentClamped + siblingCount, total);
    i++
  ) {
    pages.add(i)
  }

  const sorted = [...pages].sort((a, b) => a - b)
  const items: PaginationItem[] = []
  let prev = 0
  for (const page of sorted) {
    if (page - prev === 2) {
      // Exactly one page missing — show it rather than a lone ellipsis.
      items.push({ type: 'page', page: prev + 1 })
    } else if (page - prev > 2) {
      items.push({ type: 'ellipsis', key: `ellipsis-${prev}` })
    }
    items.push({ type: 'page', page })
    prev = page
  }
  return items
}
